import { useEffect, useState, useRef } from 'react';

import Progress from '../components/survey/Progress';
import Question from '../components/survey/Question';
import Heart from '../img/heart.png';
import Time from '../img/timer.png';
import { useParams, useHistory } from 'react-router';
import { toast } from 'react-toastify';
import config from '../Config';
import axios from 'axios';
import FinalQuestion from '../components/survey/FinalQuestion';
import { updateMainTopicsWithSubtopics } from '../util/util';

const SurveyQuestion = ({ currentUser }) => {
  // ...existing state
  const [desiredScores, setDesiredScores] = useState({}); // { [topicId]: score }
  const [topics, setTopics] = useState([]);
  const [topicsData, setTopicsData] = useState([]);
  const [selectedSubTopics, setSelectedSubTopics] = useState([]);
  const [mainTopics, setMainTopics] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [savedSurvey, setSavedSurvey] = useState(null);
  const [isLoading, setIsloading] = useState(true);
  const [isResuming, setIsResuming] = useState(false);
  const [firstQuestion, setFirstQuestion] = useState(true);
  const [resetKey, setResetKey] = useState(0);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);
  const params = useParams();
  let history = useHistory();

  // Create a unique key for the survey session based on the user's ID and the survey category.
  // This ensures that autosaved data is specific to both the user and the type of survey.
  const surveySessionKey = `survey_${currentUser?._id}_${params.category}`;

  const maxSelectedSubTopics = 3;

  const next = () => {
    trackProgress();
    setCurrentIndex(
      currentIndex === savedSurvey.answers.length
        ? savedSurvey.answers.length
        : currentIndex + 1,
    );
  };

  const previous = () => {
    if (currentIndex < 1) return;
    setCurrentIndex(currentIndex - 1);
  };

  const isNumber = (value) => {
    return !isNaN(value) && value.trim() !== '';
  };

  const onChange = (value) => {
    if (firstQuestion) setFirstQuestion(false);
    const updateSavedSurvey = { ...savedSurvey };
    updateSavedSurvey.answers[currentIndex].answer = isNumber(value)
      ? +value
      : value;

    setSavedSurvey(updateSavedSurvey);
  };

  const isChoiceSelected = (question) => {
    if (question?.type === 'multiple') {
      // Handle both old string format and new object format
      if (question?.answer === 0 || question?.answer === null || question?.answer === undefined) {
        return false; // No answer selected
      }
      
      // Valid if answer is a string (old format) or object with text property (new format)
      if (typeof question?.answer === 'string' && question?.answer.length > 0) {
        return true; // Old string format
      }
      
      if (typeof question?.answer === 'object' && question?.answer?.text) {
        return true; // New object format with score
      }
      
      return false; // Invalid answer format
    }

    return true; // For non-multiple choice questions, assume valid
  };

  const onSubTopicOptionsChange = (event) => {
    const value = event.target.value;

    if (!value) return;

    if (selectedSubTopics.includes(value)) {
      const filteredSubTopics = [...selectedSubTopics].filter(
        (topic) => value !== topic,
      );
      setSelectedSubTopics(filteredSubTopics);
    } else {
      if (selectedSubTopics.length === maxSelectedSubTopics) {
        toast('You have selected maximum sub topics', {
          type: 'info',
        });

        return;
      }
      setSelectedSubTopics([...selectedSubTopics, value]);
    }
  };

  const onSave = async () => {
    trackProgress();
    try {
      const payload = {
        answer: savedSurvey,
        favorite_topics: selectedSubTopics,
        desiredScores, // <-- send desiredScores to backend
      };
      const {
        data: { data },
      } = await axios.post(`${config.apiUrl}/api/v1/survey-histories`, payload);
      // Clear localStorage after successful save
      localStorage.removeItem(surveySessionKey);
      history.push(`/app/surveys/${data._id}/preview`);
    } catch (error) {
      toast(error?.response?.data?.message, {
        type: 'error',
      });
    }
  };

  const trackProgress = () => {
    const updateSavedSurvey = { ...savedSurvey };
    if (updateSavedSurvey.answers[currentIndex])
      updateSavedSurvey.answers[currentIndex].isTaken = true;
    setSavedSurvey(updateSavedSurvey);
    
    // Save both survey data and selectedSubTopics
    const surveyDataToSave = {
      ...updateSavedSurvey,
      selectedSubTopics: selectedSubTopics
    };
    localStorage.setItem(surveySessionKey, JSON.stringify(surveyDataToSave));
  };

  const arrangeQuestions = (questionsArray, topicsData = []) => {
    if (!Array.isArray(questionsArray) || questionsArray.length === 0)
      return [];

    if (
      !Array.isArray(topicsData) ||
      (Array.isArray(topicsData) && topicsData?.length === 0)
    )
      return questionsArray;

    // Using the already ordered topics data to group the questions.
    const arrangedQuestions = [...topicsData].map((topic) => {
      let topicQuestions = [];

      // Get questions related to the topic or sub-topics of the topic
      const questions = questionsArray.filter(
        (question) =>
          question.topic._id === topic._id ||
          question.topic.parent === topic._id,
      );

      // Get sub-topic questions and arrange them according the the ordered sub-topics of the current topic
      const subTopicQuestions = questions.filter((q) => q.topic?.parent);

      const arrangedSubQuestions = topic.subTopics.map((t) => {
        const filteredSubTopics = subTopicQuestions.filter(
          (q) => q.topic.id === t.id,
        );

        const sortedSubQuestions = filteredSubTopics.sort((a, b) => {
          return (a.order || 1) - (b.order || 1);
        });

        return sortedSubQuestions;
      });

      // Sort the main questions of the topic
      const mainTopicQuestions = questions.filter((q) => !q?.topic?.parent);

      const sortedMainTopicQuestions = mainTopicQuestions.sort((a, b) => {
        return (a.order || 1) - (b.order || 1);
      });

      // Join the questions with sorted main topic questions in front and sorted sub-topic questions behind
      topicQuestions = [
        ...sortedMainTopicQuestions,
        ...arrangedSubQuestions.flat(),
      ];

      return topicQuestions;
    });

    // Flatten sorted questions for each topic to get the well arranged questions
    const flattenedQuestions = arrangedQuestions.flat();
    
    // Remove duplicates based on question _id to prevent questions from being repeated
    const uniqueQuestions = flattenedQuestions.filter((question, index, self) => 
      index === self.findIndex(q => q._id === question._id)
    );
    
    return uniqueQuestions;
  };

  useEffect(() => {
    const initializeSurvey = async () => {
      // Reset all states when survey changes
      setSelectedSubTopics([]);
      setCurrentIndex(0);
      setMainTopics([]);
      setTopics([]);
      setTopicsData([]);

      const saved = JSON.parse(localStorage.getItem(surveySessionKey));

    if (saved != null) {
      setIsResuming(true);
      setSavedSurvey(saved);
      const previousIndex = saved.answers.findIndex(
        (item) => item.isTaken === false,
      );
      if (previousIndex >= 0) {
        setCurrentIndex(previousIndex);
      } else {
        // If all questions are answered, reset currentIndex to 0
        setCurrentIndex(0);
      }
      
      // Fetch topicsData for proper progress bar initialization
      async function fetchTopicsForResume() {
        try {
          const topicResults = await axios.get(
            `${config.apiUrl}/api/v1/survey-topics/all?grouped=1`,
          );
          if (topicResults?.data?.data?.length) {
            setTopicsData(topicResults?.data?.data);
          }
        } catch (error) {
          console.error('Failed to fetch topics for resume:', error);
        } finally {
          setIsResuming(false);
        }
      }
      
      fetchTopicsForResume();
      return;
    }
      if (saved != null) {
        setIsResuming(true);
        
        // Restore selectedSubTopics if they exist in saved data
        if (saved.selectedSubTopics && Array.isArray(saved.selectedSubTopics)) {
          setSelectedSubTopics(saved.selectedSubTopics);
        }
        
        // Remove selectedSubTopics from saved data before setting survey state
        const { selectedSubTopics: savedSubTopics, ...surveyData } = saved;
        setSavedSurvey(surveyData);
        
        const previousIndex = surveyData.answers.findIndex(
          (item) => item.isTaken === false,
        );
        if (previousIndex >= 0) {
          setCurrentIndex(previousIndex);
        } else {
          // If all questions are answered, reset currentIndex to 0
          setCurrentIndex(0);
        }
        setIsResuming(false);
        
        // Still need to load topicsData for FinalQuestion even when resuming
        try {
          const topicResults = await axios.get(
            `${config.apiUrl}/api/v1/survey-topics/all?grouped=1`,
          );
          if (topicResults?.data?.data?.length)
            setTopicsData(topicResults?.data?.data);
        } catch (error) {
          console.error('Error loading topics data:', error);
        }
        
        return;
      }

      async function fetchData() {
      try {
        const {
          data: { data },
        } = await axios.get(
          `${config.apiUrl}/api/v1/survey-questions?category=${params.category}&all=1`,
        );

        const topicResults = await axios.get(
          `${config.apiUrl}/api/v1/survey-topics/all?grouped=1`,
        );

        if (topicResults?.data?.data?.length)
          setTopicsData(topicResults?.data?.data);

        if (data?.length === 0) {
          setSavedSurvey(null);
          return;
        }

        const arrangedData = arrangeQuestions(data, topicResults?.data?.data);

        const questions = {
          _id: data.at(0)?._id,
          title: data.at(0)?.title,
          parent: data.at(0)?.parent,
          type: params.category,
          desiredScores: {}, // Add empty desiredScores object for quick surveys
          desiredScores: {}, // Add empty desiredScores object for quick surveys
          answers: arrangedData.map((item) => ({
            ...item,
            answer: 1,
            isTaken: false,
          })),
        };

        setSavedSurvey(questions);

        // Show welcome screen for extensive surveys when starting fresh
        if (params.category === 'extensive') {
          setShowWelcomeScreen(true);
        }

        // Initialize progress bars immediately
        const topics = Array.from(
          new Map(
            questions.answers.map((question) => [
              question?.topic?.title,
              {
                title: question?.topic?.title,
                parent: question?.topic?.parent,
                id: question?.topic?._id,
              },
            ]),
          ).values(),
        ).map((topic) => {
          const newItem = {
            id: topic?.id,
            text: topic?.title,
            value: 0,
            max: 0,
            isSubTopic: topic?.parent !== null,
            parent: topic?.parent,
          };
          questions.answers.forEach((item) => {
            if (item?.topic?.title === topic?.title) {
              newItem.max++;
            }
          });
          return newItem;
        });

        const mainTopics = topics.filter((item) => !item.isSubTopic);
        setMainTopics(mainTopics);
      } catch (error) {
        console.error(error);
        toast('Failed to fetch questions. Please try again later.', {
          type: 'error',
        });
      } finally {
        setIsloading(false);
      }
    }

      // Start automatic saving every 60 seconds
      const saveInterval = setInterval(() => {
        if (savedSurvey && savedSurvey.answers.length > 0) {
          trackProgress();
        }
      }, 60000);

      // Define cleanup function
      const cleanup = () => {
        clearInterval(saveInterval);
      };

      fetchData();
      return cleanup;
    };

    initializeSurvey();
  }, [params, resetKey]);

  // This useEffect is removed to prevent conflicts with the comprehensive progress bar logic below



  useEffect(() => {
    if (savedSurvey && topicsData.length > 0) {
      // Verzamel alle hoofdtopics (parent === null)
      const allMainTopics = topicsData.filter(t => !t.parent);

      // Maak een map van topicId -> { value, max }
      const topicProgressMap = {};
      savedSurvey.answers.forEach((answer) => {
        const topicId = answer?.topic?._id;
        if (!topicId) return;
        if (!topicProgressMap[topicId]) {
          topicProgressMap[topicId] = { value: 0, max: 0 };
        }
        topicProgressMap[topicId].max += 1;
        if (answer.isTaken) topicProgressMap[topicId].value += 1;
      });

      // Get unique topic IDs that are actually in the current survey
      const surveyTopicIds = new Set();
      savedSurvey.answers.forEach((answer) => {
        const topicId = answer?.topic?._id;
        const parentId = answer?.topic?.parent;
        if (topicId) surveyTopicIds.add(topicId);
        if (parentId) surveyTopicIds.add(parentId);
      });

      // Bouw mainTopics lijst met geaggregeerde voortgang
      const mainTopics = allMainTopics
        .filter(main => {
          // Only include main topics that have questions in the current survey
          // Either the main topic itself has questions, or its subtopics have questions
          const hasOwnQuestions = surveyTopicIds.has(main._id);
          const hasSubtopicQuestions = Array.isArray(main.subTopics) && 
            main.subTopics.some(sub => surveyTopicIds.has(sub._id));
          return hasOwnQuestions || hasSubtopicQuestions;
        })
        .map(main => {
          let value = 0;
          let max = 0;
          // Eigen vragen
          if (topicProgressMap[main._id]) {
            value += topicProgressMap[main._id].value;
            max += topicProgressMap[main._id].max;
          }
          // Subtopic vragen
          if (Array.isArray(main.subTopics)) {
            main.subTopics.forEach(sub => {
              if (topicProgressMap[sub._id]) {
                value += topicProgressMap[sub._id].value;
                max += topicProgressMap[sub._id].max;
              }
            });
          }
          return {
            id: main._id,
            text: main.title,
            value,
            max,
            color: main.color, // Pass color to progress bar
            isSubTopic: false,
          };
        });

      setMainTopics(mainTopics);
    }
  }, [savedSurvey, topicsData]);

  const showSaveButton = () => {
    const extensiveSurveyLength = savedSurvey?.answers?.length ?? 0;
    const quickSurveyLength = extensiveSurveyLength - 1;

    if (
      (extensiveSurveyLength === currentIndex &&
        params.category === 'extensive') ||
      (quickSurveyLength === currentIndex && params.category === 'quick')
    )
      return true;

    return false;
  };

  // Reset-knop rechtsonder
  const handleReset = () => {
    if (window.confirm('Weet je zeker dat je de survey wilt resetten? Alle voortgang wordt verwijderd.')) {
      localStorage.removeItem(surveySessionKey);
      setSavedSurvey(null);
      setCurrentIndex(0);
      setMainTopics([]);
      setTopics([]);
      setTopicsData([]);
      setSelectedSubTopics([]);
      setFirstQuestion(true);
      setShowWelcomeScreen(params.category === 'extensive'); // Reset welcome screen for extensive surveys
      setResetKey((prev) => prev + 1); // forceer opnieuw laden
      toast('De scan is gereset. Je kunt opnieuw beginnen.', { type: 'info' });
    }
  };

  // Function to handle starting the survey from welcome screen
  const handleStartSurvey = () => {
    setShowWelcomeScreen(false);
    setFirstQuestion(false);
  };

  // Welcome screen component for extensive surveys
  const renderWelcomeScreen = () => {
    return (
      <div className="relative flex flex-col justify-center min-h-screen max-w-[1600px] mx-auto px-4 sm:px-0">
        <div className="flex items-center justify-center">
          <img
            src={Heart}
            alt="extensive survey"
            className="hidden md:block w-32 h-32 lg:w-40 lg:h-40 -right-14 md:-right-4 lg:-right-0 -top-32 md:-top-14 z-[999]"
          />
        </div>
        <div
          className="flex flex-col space-y-6 sm:space-y-8 md:space-y-14 p-4 sm:p-6 md:p-12 lg:p-20"
          style={{
            backgroundColor: 'white',
            border: '4px solid rgb(0, 107, 0)',
            borderRadius: '20px',
          }}
        >
          <div className="space-y-6">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-6 max-w-4xl mx-auto">
              Welkom bij de veerkrachtscan
            </h1>
            
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-700 mb-8 max-w-4xl mx-auto">
              Krijg inzicht in jouw veerkracht en hoe je die kunt versterken
            </h2>
            
            <div className="text-left space-y-4 text-base sm:text-lg text-gray-700 max-w-4xl mx-auto">
              <p className="leading-relaxed">
                Veerkracht gaat over de ruimte die je ervaart om met veranderingen en uitdagingen om te gaan. Die ruimte noemen we handelingsruimte. De scan start daarom met vragen over jouw handelingsruimte: hoe je problemen aanpakt en oplossingen vindt, hoe je omgaat met spanning en kritiek en hoe je hoopvol en positief blijft, ook in lastige tijden.
              </p>
              
              <p className="leading-relaxed">
                Daarna kijken we naar de onderliggende factoren die jouw handelingsruimte beïnvloeden: persoonlijke factoren, omgevingsfactoren, lichamelijke factoren en zingevingsfactoren. Zo krijg je een compleet beeld van jouw veerkracht.
              </p>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Duur</h3>
                <p className="leading-relaxed">
                  Het invullen duurt ongeveer 20 minuten. Onder iedere vraag zie je je voortgang.
                </p>
                
                <h3 className="text-lg font-semibold text-gray-800">Privacy</h3>
                <p className="leading-relaxed">
                  Jouw antwoorden zijn vertrouwelijk. Ze worden niet gedeeld met anderen en alleen gebruikt voor jouw persoonlijke rapport.
                </p>
                
                <h3 className="text-lg font-semibold text-gray-800">Wat levert het je op?</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>een duidelijk beeld van jouw veerkracht op dit moment</li>
                  <li>verdieping en zelfinzicht</li>
                  <li>een overzicht van alle factoren die invloed hebben op jouw veerkracht</li>
                  <li>per factor een advies om je veerkracht te versterken</li>
                  <li>inzicht in jouw beschermende factoren: wat jou helpt om sterk te blijven</li>
                </ul>
                
              </div>
            </div>
          </div>
          
          <div className="flex justify-center mt-8">
            <button
              className="min-h-[44px] px-8 sm:px-12 py-3 sm:py-4 text-lg sm:text-xl font-medium rounded-xl transition-colors"
              style={{
                backgroundColor: 'rgb(242, 200, 60)',
                color: 'black',
                border: '1px solid black'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#efcd5b'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(242, 200, 60)'}
              onClick={handleStartSurvey}
            >
              Start de Uitgebreide Scan
            </button>
          </div>
        </div>
      </div>
    );
  };


  return (
    <>
      {/* Reset knop rechtsonder */}
      <div style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 1000 }}>
        {!firstQuestion && (
          <button
            onClick={handleReset}
            className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-3 sm:py-2 sm:px-4 rounded-full shadow-lg transition-colors text-sm sm:text-base min-h-[44px]"
            title="Survey resetten"
          >
            <span className="hidden sm:inline">Reset vragenlijst</span>
            <span className="sm:hidden">Reset</span>
          </button>
        )}
      </div>

      {savedSurvey != null ? (
        showWelcomeScreen ? (
          renderWelcomeScreen()
        ) : (
        <div className="relative flex flex-col max-w-[1600px] mx-auto px-4 sm:px-0 pt-6 sm:pt-8 md:pt-10 pb-4 sm:pb-6 md:pb-8">
          <div className="flex items-center justify-center mb-4 md:mb-0">
            <img
              src={params.category === 'quick' ? Time : Heart}
              alt="question type"
              className="w-16 h-16 sm:w-24 sm:h-24 md:w-32 md:h-32 lg:w-40 lg:h-40 z-[999]"
            />
          </div>
          <div
            className="flex flex-col space-y-16 sm:space-y-8 md:space-y-1 p-4 sm:p-6 md:p-8 lg:p-16"
            style={{
              backgroundColor: 'white',
              border: `4px solid ${(() => {
                try {
                  const currentTopic = savedSurvey?.answers[currentIndex]?.topic;
                  if (!currentTopic) {
                    console.log('No current topic, using fallback');
                    return 'rgb(0, 107, 0)';
                  }
                  
                  // Use the same color source as the Progress component (mainTopics array)
                  const mainTopicId = currentTopic.parent || currentTopic._id;
                  const mainTopicProgress = mainTopics.find(mt => mt.id === mainTopicId);
                  
                  if (mainTopicProgress?.color) {
                    console.log('Using mainTopics color:', mainTopicProgress.color);
                    return mainTopicProgress.color;
                  }
                  
                  // Fallback to topic color
                  if (currentTopic.color) {
                    console.log('Using topic color:', currentTopic.color);
                    return currentTopic.color;
                  }
                  
                  console.log('No color found, using green fallback');
                  return 'rgb(0, 107, 0)';
                } catch (error) {
                  console.error('Error in border color logic:', error);
                  return 'rgb(0, 107, 0)';
                }
              })()}`,
              borderRadius: '20px',
            }}
          >
            {savedSurvey?.answers?.length === currentIndex && params.category === 'extensive' ? (
              <FinalQuestion
                topicsData={topicsData}
                selectedSubTopics={selectedSubTopics}
                onChange={onSubTopicOptionsChange}
              />
            ) : savedSurvey?.answers?.length === currentIndex && params.category === 'quick' ? (
              // For quick surveys, show completion message instead of FinalQuestion
              <div className="text-center py-8">
                <h2 className="text-2xl font-bold text-green-600 mb-4">Survey voltooid!</h2>
                <p className="text-lg text-gray-700">Bedankt voor het invullen van de snelle scan.</p>
              </div>
            ) : (
              <Question
                index={currentIndex}
                firstQuestion={firstQuestion}
                answer={savedSurvey?.answers[currentIndex]?.answer}
                onChange={onChange}
                type={savedSurvey?.answers[currentIndex]?.type}
                allAnswers={savedSurvey.answers}
                isLastQuestionInSurvey={currentIndex === savedSurvey.answers.length - 1}
                topicsData={topicsData}
                desiredScores={desiredScores}
                setDesiredScores={setDesiredScores}
                isExtendedSurvey={params.category === 'extensive'}
                onNext={next}
                {...savedSurvey?.answers[currentIndex]}
              />
            )}

            <div className="flex justify-between w-full gap-4 mt-8">
              <button
                className="btn btn-outline hover:bg-black text-black hover:text-white btn-rounded min-h-[44px] px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base font-medium flex-1 sm:flex-none"
                onClick={previous}
                disabled={!currentIndex}
              >
                Vorige
              </button>

              {showSaveButton() ? (
                <button
                  className="button hover:bg-black min-h-[44px] px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base font-medium flex-1 sm:flex-none"
                  onClick={onSave}
                  disabled={
                    selectedSubTopics.length !== maxSelectedSubTopics &&
                    params.category === 'extensive'
                  }
                >
                  Opslaan
                </button>
              ) : (
                <button
                  className="button hover:bg-black min-h-[44px] px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base font-medium flex-1 sm:flex-none"
                  onClick={next}
                  disabled={
                    !isChoiceSelected(savedSurvey?.answers[currentIndex])
                  }
                >
                  Volgende
                </button>
              )}
            </div>
          </div>

          <div className="w-full flex px-4 sm:px-6 md:px-10 lg:px-20 mt-4 sm:mt-6 md:mt-10">
            <div className="md:w-[90%] w-full flex flex-wrap gap-y-3 sm:gap-y-4 gap-x-2 sm:gap-x-4">
              {mainTopics.map((item, index) => (
                <Progress
                  title={item.text}
                  max={item.max}
                  value={item.value}
                  color={item.color}
                  key={index}
                />
              ))}
            </div>
          </div>
        </div>
        )
      ) : (
        <>
          {!isLoading && !savedSurvey && (
            <div className="h-screen pt-32 px-4">
              <h6>Er is geen vraag gevonden voor deze categorie</h6>
            </div>
          )}
        </>
      )}
    </>
  );
};
export default SurveyQuestion;
